import React, { useEffect, useState } from "react";
import { useSelector, useDispatch, connect } from "react-redux";
import { reduxForm, Field, isValid } from "redux-form";
import { push } from "connected-react-router";
import {
	Modal,
	Button,
	ReduxControls,
	Layout,
	ProgressIndicator,
	ErrorBanner
} from "rhombus-react";

import { loadLibrary, resetForm, createLibrary, updateLibrary } from "reducers/LibraryProperties";
import { loadAdvertiserDetails, resetDetails } from "reducers/AdvertiserDetails";
import { loadDSPConnections } from "reducers/Social";
import { getAdvertiserAutoClassificationEnabled } from "selectors/Intelligence";
import { LIBRARY_PROPERTIES } from "forms/list";
import Validators, {
	validateForm,
	CONTENT_LIMIT_MAX
} from "forms/validators/LibraryProperties";
import Permissioned from "./Permissioned";
import { useClearLibraryFilters } from "hooks/useClearFilters";

import { CREATE, EDIT, tooltipProps, textTooltips, libraryNameTooltipProps, includeUmlauts } from "widgets/modals/library/helpers";
import AdvertiserGroup from "widgets/modals/library/AdvertiserGroup";
import QALocation from "widgets/modals/library/QALocation";
import SelfApproval from "widgets/modals/library/SelfApproval";
import KpiSelector from "widgets/modals/library/KpiSelector";
import { saveLibraryKpis } from "reducers/PredictiveScoring";
import { usePredictiveScoringEnabled } from "hooks/usePredictiveScoringEnabled";
import { isCreatives as isCreativesView } from "widgets/toolbars/CreativesInfoHeader";
import useCancelToken from "hooks/useCancelToken";

import "./LibraryPropertiesModal.css";

const creativesListPath = "/library/";


const emailTooltips={
	...tooltipProps,
	description:"Please enter single valid email address",
};

const nameProps = {
	name: "name",
	label: "Creative Library Name",
	placeholder: "Enter Library Name",
	tip: libraryNameTooltipProps,
	required: true,
	showCharCount:true,
	charMax:250,
	validate:Validators.name,
	component: ReduxControls.Input,
	props:{ required:true },
};

const brandProps = {
	name: "brand",
	label: "Brand / Product",
	placeholder: "Enter Brand / Product Name",
	tip: textTooltips,
	showCharCount:true,
	charMax:250,
	component: ReduxControls.Input,
};

const classificationProps = {
	name: "isClassificationEnabled",
	useValue: true,
	hasHeader: true,
	label: "Classification",
	inlineLabel: "Enable Automated Classification (Beta)",
	tooltipVersion: 1, // tooltipVersion: 2 causes issues with tests complaining about crypto.getRandomValues() not being supported
	inlineTip: {
		description: "Automated application of Creative Labels to Creatives and Versions via creative auto-classification AI.",
		direction: "top",
	},
	component: ReduxControls.Checkbox,
};

const emailProps = {
	name: "email",
	label: "Primary Contact",
	tip: emailTooltips,
	placeholder: "example@address.com",
	required: true,
	component: ReduxControls.Input,
};

const contentLimitProps = {
	name: "contentLimitInKilobytes",
	label: "Creative File Size Warning Threshold (Open Web Display, in KB)",
	required: true,
	max:CONTENT_LIMIT_MAX,
	component: ReduxControls.Input,
	tip:{
		...tooltipProps,
		description:"A warning will be displayed within the creative list view for open web display creatives that exceed this limit in total size (creative + the default version, if dynamic)",
	},
	props:{ type:"number" },
};


const mediaBuyerName = {
	name: "mediaBuyerName",
	label: "Media Buyer",
	placeholder: "Enter Media Buyer Name",
	tip: includeUmlauts,
	showCharCount:true,
	charMax:250,
	component: ReduxControls.Input,
};
const mediaBuyerEmail = {
	name: "mediaBuyerEmail",
	label: "Media Buyer Email",
	placeholder: "example@address.com",
	tip: emailTooltips,
	component: ReduxControls.Input,
};


function advertiserLabel(advertiser){
	let { id, name } = advertiser;
	return `${name} (${id})`;
}

function LibraryPropertiesModal({
	libraryId,
	handleSubmit,
	canSetQa,
	isInternal,
	isOwner,
	advertiser,
	advertiserOptions,
	isFormValid,
	formErrors,
	returnPath = "", // Route path to return to after saving or canceling
	mode,
	disableMediaBuyerName,
}){
	let title = (mode === CREATE) ? "New Library" : "Edit Library Settings"
		, dispatch = useDispatch()
		, { isLoaded, selfApproval } = useSelector(state => ({
			isLoaded: state.libraryProperties?.isLoaded,
			selfApproval: state.libraryProperties?.selfApproval,
		}))
		, canEditAdvertiser = (mode === EDIT && (isInternal || isOwner))
		, bladeProps = {
			focusTrapPaused: true,
		}
		;
	const isAdvertiserClassificationEnabled = useSelector(getAdvertiserAutoClassificationEnabled);
	const isPredictiveScoringOn = usePredictiveScoringEnabled();
	const [kpiPayload, setKpiPayload] = useState([]);

	const advertiserDetails = useSelector(state => state.advertiserDetails || {});
	const libraryAdvertiserId = advertiser?.id;
	const isAdvertiserDetailsLoaded = advertiserDetails.isLoaded;
	const loadedAdvertiserId = advertiserDetails.data?.id;
	// resetForm() clears libraryProperties (including `advertiser`) on every mount, so libraryAdvertiserId
	// is transiently falsy even for a library that DOES have one, until loadLibrary(libraryId) resolves.
	// Gate the "no advertiser" branch below on this library having actually finished loading, so we don't
	// treat "not loaded yet" as "no advertiser" and clear (then immediately re-fetch) valid advertiser data.
	const loadedLibraryId = useSelector(state => state.libraryProperties?.id);

	const clearFilters = useClearLibraryFilters();

	const { getNewToken, cancel } = useCancelToken(true);

	useEffect(() => {
		dispatch(resetForm());
		if(mode === EDIT){
			dispatch(loadLibrary(libraryId, getNewToken()));
			return cancel;
		}
	}, [dispatch, mode, libraryId]);

	useEffect(() => {
		/*
			The advertiser details may not be loaded if we're editing from the libraries route, so we fetch them
			if the `advertiserId` is different from the one in the `advertiserDetails` state.
		*/
		if(mode === EDIT && libraryAdvertiserId){
			if((isAdvertiserDetailsLoaded && (libraryAdvertiserId !== loadedAdvertiserId))){
				dispatch(loadAdvertiserDetails(libraryId));
			}
		} else if(mode === EDIT && loadedLibraryId === Number(libraryId) && !libraryAdvertiserId && loadedAdvertiserId){
			// A library with no advertiser has nothing to load, but advertiserDetails may still hold
			// data left over from a previously viewed advertiser-owning library. Clear it so
			// advertiser-scoped features (e.g. predictive scoring) never evaluate against stale data.
			dispatch(resetDetails());
		}
	}, [mode, libraryAdvertiserId, loadedAdvertiserId, isAdvertiserDetailsLoaded, libraryId, loadedLibraryId]);

	function closeModal(){
		dispatch(push(returnPath));
	}

	function submitForm(values){
		if(mode === EDIT){
			if (isPredictiveScoringOn && !kpiPayload.length) {
				return;
			}
			return dispatch(updateLibrary(libraryId, values))
				.then((resp) => {
					if(resp.success) {
						if (isPredictiveScoringOn && kpiPayload.length) {
							dispatch(saveLibraryKpis(libraryId, kpiPayload));
						}
						if (isCreativesView(null, { pathname: returnPath })) {
							dispatch(loadDSPConnections(libraryId));
						}
						return dispatch(closeModal());
					}
				});
		} else {
			return dispatch(createLibrary(values))
				.then((resp) => {
					if(resp.success){
						clearFilters();
						return dispatch(push(`${creativesListPath}${resp.payload.data.id}/`));
					}
				});
		}
	}

	function Fields(){
		let $errorBanner = null;
		if(formErrors){
			let errors = [];

			if(formErrors.description){
				errors = [formErrors.description];
			} else {
				// TODO: I'm not sure if the API will ever return errors like this?
				errors = Object.keys(formErrors).map((v)=> {
					return formErrors[v];
				});
			}

			$errorBanner = <ErrorBanner title="Errors in the Form" messages={errors} type="library" />;
		}

		return [
			<ProgressIndicator key="loading" isLoading={!isLoaded || !advertiserDetails.isLoaded} />
			, <form key="form" id={LIBRARY_PROPERTIES} onSubmit={handleSubmit(values => submitForm(values))}>
				{$errorBanner}
				<Field { ...nameProps} />
				<AdvertiserGroup mode={mode} canEditAdvertiser={canEditAdvertiser} advertiser={advertiser} advertiserOptions={advertiserOptions}  />
				<Field {...brandProps} />
				{mode === EDIT && <KpiSelector libraryId={libraryId} onKpiChange={setKpiPayload} />}
				{advertiser ? <Field
					{...classificationProps}
					disabled = {!isAdvertiserClassificationEnabled}
				/> : null}
				<Field {...emailProps} />
				<Field {...mediaBuyerName} disabled={disableMediaBuyerName} />
				<Field {...mediaBuyerEmail} />
				<Permissioned domain="Creative Library" action="Increase File size limit">
					<Field {...contentLimitProps} />
				</Permissioned>
				<QALocation mode={mode} canSetQa={canSetQa} />
				<SelfApproval mode={mode} value={selfApproval} />
			</form>
		];
	}

	function Footer(){
		return (
			<Layout.ModalFooter>
				<Button
					theme="default"
					type="reset"
					onClick = { closeModal }
				>Cancel</Button>
				<Button
					type="submit"
					theme="green"
					disabled={!isFormValid || (isPredictiveScoringOn && !kpiPayload.length)}
					form={LIBRARY_PROPERTIES}
				>Save</Button>
			</Layout.ModalFooter>
		);
	}

	return (
		<Modal
			title = { title }
			xClose = { true }
			className="library-properties-modal"
			allowFullScreen = { false }
			renderContent = { Fields }
			renderFooter = { Footer }
			onClose = { closeModal }
			setAppTitle={false}
			bladeProps={bladeProps}
		/>
	);
}

LibraryPropertiesModal.displayName = "LibraryPropertiesModal";
LibraryPropertiesModal.defaultProps = {
	isFormValid:true,
	errors:null,
};

function mapStateToProps(state, ownProps){
	let {
			/* We extract out the following so that ReduxForm doesn't re-initalize on error */
			error,
			isLoaded,
			hasErrorLoading,
			success,
			advertisers = [],
			hasErrorLoadingAdvertisers,
			...initialValues } = state.libraryProperties
		, { you } = state
		, canSetQa = false
		, isOwner = (you.businessEntityId === initialValues.mediaBuyer?.id)
		, isFormValid = isValid(LIBRARY_PROPERTIES)(state)
		, IV = Object.assign({}, initialValues)
		;
	const mode = ownProps.libraryId ? EDIT : CREATE;
	const mediaBuyerNameValue = state.libraryProperties?.mediaBuyerName;
	const disableMediaBuyerName = (mode === EDIT && (!!mediaBuyerNameValue)); // MediaBuyerName cannot be modified once set

	// Set the email to the user's email
	if(!initialValues.email){
		IV.email = you.email;
	}

	if(you.departmentId === 0){
		canSetQa = true;
	} else {
		if(!IV.qaLocation){
			IV.qaLocation = you.departmentId;
		}
	}

	let advertiserOptions = advertisers.map((advertiser) => {
		let { id, name } = advertiser;
		return {
			label: advertiserLabel(advertiser),
			value: {
				id,
				name,
			},
		};
	});

	if(IV.advertiser && IV.advertiser.id){
		IV.advertiser = {
			...IV.advertiser,
			label:IV.advertiser.name,
			value:IV.advertiser.id,
		};
	}

	return {
		initialValues:IV,
		isFormValid,
		canSetQa,
		isLoaded,
		isInternal: you.isInternal,
		isOwner,
		advertiser: initialValues.advertiser,
		advertiserOptions,
		hasErrorLoading: (!success || hasErrorLoading || hasErrorLoadingAdvertisers),
		formErrors:error,
		mode,
		disableMediaBuyerName,
	};
}

export default connect(mapStateToProps)(
	reduxForm({
		form: LIBRARY_PROPERTIES,
		validate:validateForm,

		// Force sync validation to recompute on every render. Without this, redux-form keeps the
		// syncErrors it computed against the empty initial values and never revalidates after the
		// library's values load asynchronously, leaving isFormValid stuck false for a valid form.
		shouldValidate:     () => true,
		pure:               false,
		enableReinitialize: true,
		touchOnBlur:        true,
		touchOnChange:      true,
		onSubmitFail(errors, dispatch){
			console.log("Errors from form...");
			console.log(errors);
		},
	})(LibraryPropertiesModal)
);

export const PRIVATE = {
	mapStateToProps,
	LibraryPropertiesModal,
};
